import { inject } from '@angular/core';
import { of } from 'rxjs';
import {
    ActionsSuggestionsCommand,
    extractCommandsFromToolResult,
    Interceptor,
    isToolCompleteCommand,
    PromptResponse,
    ResponseCommand
} from '@panaya/ai-chat/domain';
import { AiChatActionsSuggestionsService } from './ai-chat-actions-suggestions.service';
import { AiChatActionsSuggestionsProviderService } from './ai-chat-actions-suggestions-provider.service';

/**
 * Creates an interceptor that extracts and processes action suggestions from prompt responses.
 * Handles both old server format (top-level commands) and new server format (tool_complete wrapper).
 *
 * The interceptor:
 * - Expands tool_complete commands to extract nested action suggestions
 * - Processes raw suggestion keys into UI-ready action suggestions
 *
 * @returns Interceptor function that processes action suggestions
 */
export function createActionsSuggestionsResponseInterceptor(): Interceptor<PromptResponse> {
    const actionsSuggestionsService = inject(AiChatActionsSuggestionsService);
    const providerService = inject(AiChatActionsSuggestionsProviderService);

    return (response: PromptResponse) => {
        // Extract from both toolResult (new format) and top-level (old format)
        const rawSuggestions = extractActionsSuggestions(response.commands);

        const processedSuggestions = rawSuggestions ? actionsSuggestionsService.getProcessedSuggestions(rawSuggestions) : undefined;

        providerService.notifyActionsSuggestions(processedSuggestions);

        return of(response); // Returns unmodified response
    };
}

/**
 * Extracts action suggestions from both server formats.
 * Handles the new server format (tool_complete) and old format (top-level) seamlessly.
 *
 * @param commands - Array of response commands
 * @returns Array of suggestion keys or null if none found
 */
function extractActionsSuggestions(commands: ResponseCommand[]): string[] | null {
    const allCommands = commands.flatMap((command) => {
        if (isToolCompleteCommand(command)) {
            // Parse toolResult and extract nested commands
            return extractCommandsFromToolResult(command.tool_result) ?? [];
        }
        // Pass through regular commands
        return [command];
    });

    const actionsCmd = allCommands.find(isActionsSuggestionsCommand);
    return actionsCmd?.actions_suggestions ?? null;
}

/**
 * Type guard to check if a command is an ActionsSuggestionsCommand
 */
function isActionsSuggestionsCommand(command: ResponseCommand): command is ActionsSuggestionsCommand {
    return command.type === 'actions_suggestions';
}
